import type { StaffImportValidationSettings } from "@lafayette/schemas";

// The settings of a validation run that a staff member may give when the mapping is submitted (W5,
// `validation`). Every one is optional. The server uses `catalog_import`, no default zone and `ISO_ONLY`
// for what is not given. The model-assist mode is never set here: the server decides it.

export type ValidationSettingsForm = Readonly<{
  sourceSystem: string;
  defaultTimeZone: string;
  dateOrder: "ISO_ONLY" | "DMY" | "MDY";
}>;

export const DEFAULT_SOURCE_SYSTEM = "catalog_import";

export const emptyValidationSettings: ValidationSettingsForm = {
  sourceSystem: "",
  defaultTimeZone: "",
  dateOrder: "ISO_ONLY",
};

export type ValidationSettingsIssues = Readonly<{
  sourceSystem?: string;
  defaultTimeZone?: string;
}>;

const SOURCE_SYSTEM = /^[a-z][a-z0-9_]{0,63}$/;

/** An IANA zone such as `Asia/Kolkata`. The browser knows the list, so no list is kept here. */
export function isTimeZone(value: string): boolean {
  if (!/^[A-Za-z][A-Za-z0-9_+-]*(\/[A-Za-z0-9_+-]+)*$/.test(value) || value.length > 64) {
    return false;
  }
  try {
    new Intl.DateTimeFormat("en", { timeZone: value });
    return true;
  } catch {
    return false;
  }
}

export function settingsIssues(form: ValidationSettingsForm): ValidationSettingsIssues {
  const sourceSystem = form.sourceSystem.trim();
  const zone = form.defaultTimeZone.trim();
  return {
    ...(sourceSystem !== "" && !SOURCE_SYSTEM.test(sourceSystem)
      ? {
          sourceSystem:
            "Use lower-case letters, digits and underscores. Start with a letter. At most 64 characters.",
        }
      : {}),
    ...(zone !== "" && !isTimeZone(zone)
      ? { defaultTimeZone: "This is not a time zone. Use a name such as Asia/Kolkata." }
      : {}),
  };
}

/** The body part for W5, or `undefined` when staff left every setting at its default. */
export function toSubmitSettings(
  form: ValidationSettingsForm,
): StaffImportValidationSettings | undefined {
  const sourceSystem = form.sourceSystem.trim();
  const zone = form.defaultTimeZone.trim();
  const settings: StaffImportValidationSettings = {
    ...(sourceSystem === "" ? {} : { sourceSystem }),
    ...(zone === "" ? {} : { defaultTimeZone: zone }),
    ...(form.dateOrder === "ISO_ONLY" ? {} : { dateOrder: form.dateOrder }),
  };
  return Object.keys(settings).length === 0 ? undefined : settings;
}
